import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Button, Typography, Alert, Dialog, DialogContent, DialogActions, Link
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDropzone } from 'react-dropzone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReCAPTCHA from 'react-google-recaptcha';
import FileList from './FileList';
import ContactForm from './ContactForm';
import { uploadFiles } from '../api/uploadService';

export default function UploadForm() {
  const MAX_SIZE = 10 * 1024 * 1024; // 20MB
  const MAX_FILES = 5; // Maximum number of files
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;
  const recaptchaRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [contact, setContact] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    company: '', 
    telephone: '',
    description: ''
  });
  const [status, setStatus] = useState({
  open: false,
  success: false,
  msg: '',
  details: []
  }); 
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const [dropErrors, setDropErrors] = useState([]);

  const onDrop = useCallback(
    (accepted, rejected) => {
      // custom total‐files check
      if (files.length + accepted.length > MAX_FILES) {
        setDropErrors([`Cannot select more than ${MAX_FILES} files in total.`]);
        return;
      }
      // clear any previous drop error
      setDropErrors([]);

      // accept only those under MAX_SIZE
      const underSize = accepted.filter(f => f.size <= MAX_SIZE);
      setFiles(curr => [...curr, ...underSize]);
    },
    // include ALL external values you reference
    [files, MAX_FILES, MAX_SIZE]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: MAX_SIZE,
  });

  const removeFile = i =>
    setFiles(curr => curr.filter((_, idx) => idx !== i));

  const handleContactChange = e =>
    setContact({ ...contact, [e.target.name]: e.target.value });

  const handleFormatDialogOpen = () => {
    setFormatDialogOpen(true);
  };

  const handleFormatDialogClose = () => {
    setFormatDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (!contact.firstName || !contact.lastName || !contact.email || !contact.company || !contact.telephone || files.length === 0) {
      setStatus({ open: true, success: false, msg: 'Please fill all fields & add files.' });
      return;
    }
    
    if (!recaptchaToken) {
      setStatus({ open: true, success: false, msg: 'Please complete the reCAPTCHA verification.' });
      return;
    }
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    Object.entries(contact).forEach(([k, v]) => formData.append(k, v));
    formData.append('recaptchaToken', recaptchaToken);

    setLoading(true);

    try {
      await uploadFiles(formData);
      setStatus({ open: true, success: true, msg: 'Upload successful!', details: [] });
      setFiles([]);
      setContact({ firstName: '', lastName: '', email: '', company: '', telephone: '', description: '' });
      setRecaptchaToken(null);
      // Reset the reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      } catch (err) {
        console.error('Upload error:', err.response || err);

        const errMsg =
          err.response?.data?.error ||
          err.response?.statusText ||
          err.message ||
          'Upload failed.';

        const errDetails = err.response?.data?.details || [];

        setStatus({
          open: true,
          success: false,
          msg: errMsg,
          details: errDetails
        });
      }
      finally {
        setLoading(false);
      }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, p: 3, bgcolor: 'background.default', borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Upload Your Files
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Please upload files (max 10 MB each, max 5 files). Follow the instructions for the required format{' '}
        <Link 
          component="button" 
          variant="body2" 
          onClick={handleFormatDialogOpen}
          sx={{ cursor: 'pointer' }}
        >
          here
        </Link>
        .
      </Typography>

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: 'primary.main',
          p: 4, textAlign: 'center',
          bgcolor: isDragActive ? 'primary.light' : 'background.default',
          cursor: 'pointer',
          mb: 3
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        <Typography>
          {isDragActive
            ? 'Drop files here…'
            : 'Drag & drop files here, or click to select'}
        </Typography>
      </Box>

      {fileRejections.length > 0 && (
        <Box sx={{ mt: 2, color: 'error.main' }}>
          <Typography variant="subtitle2">File upload issues:</Typography>
          <ul>
            {fileRejections.map(({ file, errors }) =>
              errors.map((e, i) => (
                <li key={`${file.name}-${i}`}>
                  <Typography variant="body2">
                    {file.name}: {e.message}
                  </Typography>
                </li>
              ))
            )}
          </ul>
        </Box>
      )}

      {/* show custom total‐files limit error */}
      {dropErrors.length > 0 && (
        <Box sx={{ mt: 2, color: 'error.main' }}>
          {dropErrors.map((err, i) => (
            <Typography key={i} variant="body2">
              {err}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <FileList files={files} onRemove={removeFile} />
      </Box>

      <Box sx={{ mb: 4 }}>
        <ContactForm values={contact} onChange={handleContactChange} />
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'left' }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={token => setRecaptchaToken(token)}
          onExpired={() => setRecaptchaToken(null)}
        />
      </Box>

      <LoadingButton
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        fullWidth
        disabled={!recaptchaToken}
        loading={loading}
      >
        Submit
      </LoadingButton>

      {/* ─── Inline feedback ─── */}
      {status.open && (
        <Box sx={{ mt: 3 }}>
          <Alert
            severity={status.success ? 'success' : 'error'}
            sx={{ '& .MuiAlert-message': { flexDirection: 'column', alignItems: 'flex-start' } }}
          >
            <Box component="span" sx={{ fontWeight: 'bold', mb: 1 }}>
              {status.msg}
            </Box>

            {/* Render all details here, inside the same Alert */}
            {status.details?.length > 0 && (
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {status.details.map((d, i) => (
                  <Typography
                    key={i}
                    component="li"
                    variant="body2"
                    color="inherit"      // keep it the same color as the Alert text
                    sx={{ lineHeight: 1.4 }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        </Box>
      )}

      {/* Format Instructions Dialog */}
      <Dialog 
        open={formatDialogOpen} 
        onClose={handleFormatDialogClose}
        maxWidth="md"
        fullWidth
      >
        {/* <DialogTitle>File Format Requirements</DialogTitle> */}
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Supported File Formats:
          </Typography>
          <Typography variant="body2" paragraph>
            • CSV (Comma-separated values)
            <br />
            • XLSX (Excel spreadsheet)
            <br />
            • TXT (Plain text files)
          </Typography>

          <Typography variant="h6" gutterBottom>
            File Requirements:
          </Typography>
          <Typography variant="body2" paragraph>
            • Maximum file size: 10 MB per file
            <br />
            • Maximum number of files: 5 files
            <br />
            • Files should contain clean, structured data
          </Typography>

          <Typography variant="h6" gutterBottom>
            Data Format Guidelines:
          </Typography>
          <Typography variant="body2" paragraph>
            • For CSV and TXT files: Use comma (,) as delimiter
            <br />
            • Ensure data consistency across rows
            <br />
            • Remove any empty rows or columns
            <br />
            • Use UTF-8 encoding for special characters
            <br />
            • Include the following column headers in the first row with corresponding description and data format:
              <Box 
              component="pre" 
              sx={{ 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1, 
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.7rem'
              }}
              >
{
`Date          : Date in YYYY-MM-DD format                                         2025-06-17
Time          : Time in HH:MM:SS format                                           14:30:06
Program name  : Any arbitrary text                                                Program A, Program B
Class         : Weight range in grams, hyphenated; use * if unspecified           120-200
Gate          : Gate number assignment; use 0 for rejected pieces                 0, 1, 2, ..
Type          : Indicates whether the row represents a piece or a batch           Piece, Batch
Weight        : Weight of the piece or batch in grams                             247, 5018
Batch count   : Number of pieces required for the batch; use * if unspecified     30, <30, >30
Batch weight  : Weight range in grams of the batch; use * if unspecified          4875-5025
Priority      : (Optional) Numeric value indicating allocation priority           0, 50, 120`
}
              </Box>
          </Typography>

          <Typography variant="h6" gutterBottom>
            Example CSV/TXT Structure:
          </Typography>
          <Box 
            component="pre" 
            sx={{ 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1, 
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.7rem'
            }}
          >
{
`Date,Time,Program name,Class,Gate,Type,Weight,Batch count,Batch weight,Priority
2025-06-17,14:30:06,Program A,120-200,1,Piece,247,30,4875-5025,0
2025-06-17,14:30:06,Program A,120-200,2,Piece,198,*,4875-5025,0
2025-06-17,14:30:07,Program B,160-240,7,Piece,247,<30,1950-2050, 0
2025-06-17,14:30:08,Program C,*,5,Piece,247,>30,4875-5025, 0
2025-06-17,14:30:08,Program B,170-210,8,Piece,233,<30,1950-2050, 0
2025-06-17,14:30:09,Program B,170-210,8,Batch,1998,<30,1950-2050, 0
2025-06-17,14:30:08,Program C,120-190,4,Piece,247,30,*, 0
2025-06-17,14:30:09,*,*,0,Piece,247,*,*, 0
`
}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFormatDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}